import type { VolumeProfileAction } from './actions';
import { createInitialVolumeProfileDataState } from './types';
import type { VolumeProfileDataState, VolumeProfileState, VolumeProfileSubject } from './types';

function sameSubject(a: VolumeProfileSubject | null, b: VolumeProfileSubject | null): boolean {
    return a === b || (a !== null && b !== null && a.symbol === b.symbol && a.range === b.range);
}

function withData(state: VolumeProfileState, data: VolumeProfileDataState): VolumeProfileState {
    return data === state.data ? state : { ...state, data };
}

export function reducer(state: VolumeProfileState, action: VolumeProfileAction): VolumeProfileState {
    switch (action.type) {
        case 'SET_TAB':
            if (state.tabId === action.tabId) return state;
            return { ...state, tabId: action.tabId };
        case 'SET_VIEW_MODE':
            if (state.viewMode === action.viewMode) return state;
            return { ...state, viewMode: action.viewMode };
        case 'SET_SESSION':
            if (state.session === action.session) return state;
            return { ...state, session: action.session };
        case 'SET_RANGE':
            if (state.range === action.range) return state;
            return { ...state, range: action.range };
        case 'SET_INCREMENT':
            if (state.increment === action.increment) return state;
            return { ...state, increment: action.increment };
        case 'TOGGLE_OVERLAY':
            return {
                ...state,
                overlays: {
                    ...state.overlays,
                    [action.overlay]: !state.overlays[action.overlay],
                },
            };
        case 'SUBJECT_CHANGED': {
            if (sameSubject(state.data.subject, action.subject)) return state;
            // History and summaries belong to the old subject. The stream is per symbol, so a
            // range-only change keeps its open bar (the merge decides whether it is in the window)
            // and the live status and profile survive because the owner re-points the stream itself.
            const sameSymbol = state.data.subject?.symbol === action.subject?.symbol;
            return withData(state, {
                ...createInitialVolumeProfileDataState(action.subject),
                liveBar: sameSymbol ? state.data.liveBar : null,
                liveStatus: state.data.liveStatus,
                liveProfile: state.data.liveProfile,
            });
        }
        case 'HISTORY_LOADING':
            // The query reports no data at all (first load, or a cache reset by QueryCacheUserService);
            // a background refetch keeps its stale data and never arrives here.
            if (state.data.historyStatus === 'loading' && state.data.history === null) return state;
            return withData(state, { ...state.data, historyStatus: 'loading', history: null, historyError: null });
        case 'HISTORY_LOADED':
            if (state.data.history === action.snapshot) return state;
            return withData(state, {
                ...state.data,
                historyStatus: 'ready',
                history: action.snapshot,
                historyError: null,
            });
        case 'HISTORY_FAILED':
            // Reached only when the query has no data (a refetch failure keeps stale data and is
            // delivered as a loaded result), so whatever was shown is gone.
            if (state.data.historyStatus === 'error' && state.data.historyError === action.error) return state;
            return withData(state, {
                ...state.data,
                historyStatus: 'error',
                history: null,
                historyError: action.error,
            });
        case 'SUMMARY_LOADED':
            if (state.data.summaryRows === action.rows) return state;
            return withData(state, { ...state.data, summaryRows: action.rows });
        case 'LIVE_BAR_UPDATED': {
            const { bar } = action;
            if (bar === state.data.liveBar) return state;
            // A bar from a stream that has not caught up with a symbol change is stale.
            if (bar !== null && state.data.subject !== null && bar.symbol !== state.data.subject.symbol) return state;
            return withData(state, { ...state.data, liveBar: bar });
        }
        case 'LIVE_STATUS_CHANGED':
            if (state.data.liveStatus === action.status) return state;
            return withData(state, { ...state.data, liveStatus: action.status });
        case 'LIVE_PROFILE_CHANGED':
            if (state.data.liveProfile === action.profile) return state;
            return withData(state, { ...state.data, liveProfile: action.profile });
        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
